import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Facebook, Twitter, MessageCircle, Mail, Link2 } from "lucide-react";
import { MagNav, ArticleCard, SectionTitle, C, serif, sans } from "@/components/magazine/pubShared";
import { RadioPlayerBar } from "@/components/magazine/RadioPlayerBar";
import { SubscribeBand } from "@/components/magazine/pubShared";

const B = process.env.REACT_APP_BACKEND_URL;

export default function PublicMagArticle() {
  const { pubSlug, articleSlug } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    axios.get(`${B}/api/mag/public/${pubSlug}/article/${articleSlug}`)
      .then((r) => {
        setA(r.data);
        document.title = r.data.seo_title || r.data.title;
        const ref = document.referrer || "";
        const source = /facebook|twitter|t\.co|instagram|linkedin/.test(ref) ? "social" : /google|bing|duckduckgo/.test(ref) ? "search" : ref.includes(window.location.host) ? "internal" : "direct";
        axios.post(`${B}/api/mag/public/${pubSlug}/view`, { article_id: r.data.article_id, source }).catch(() => {});
      })
      .catch(() => setErr(true));
    window.scrollTo(0, 0);
  }, [pubSlug, articleSlug]);

  if (err) return <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg, ...serif }}>Story not found.</div>;
  if (!a) return <div className="min-h-screen" style={{ background: C.bg }} />;

  const url = window.location.href;
  const share = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    x: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(a.title)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(a.title + " " + url)}`,
    email: `mailto:?subject=${encodeURIComponent(a.title)}&body=${encodeURIComponent(url)}`,
  };
  const copyLink = () => { navigator.clipboard.writeText(url); toast.success("Link copied"); };

  return (
    <div className="min-h-screen pb-24" style={{ background: C.bg, ...sans }} data-testid="public-mag-article">
      <MagNav pub={a.publication} slug={pubSlug} />
      <article className="max-w-4xl mx-auto px-4 sm:px-8 py-12">
        <div className="text-[11px] uppercase tracking-[0.25em] font-semibold mb-4" style={{ color: C.terra }}>
          {(a.categories || []).join(" · ") || "Feature"}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none" style={{ ...serif, color: C.ink }} data-testid="mag-article-title">{a.title}</h1>
        {a.subtitle && <p className="mt-5 text-xl leading-relaxed" style={{ color: C.ink2 }}>{a.subtitle}</p>}
        <div className="mt-6 flex flex-wrap items-center gap-3 text-sm" style={{ color: C.muted }}>
          {a.author_image && <img src={a.author_image} alt="" className="h-9 w-9 rounded-full object-cover" />}
          <span className="font-semibold" style={{ color: C.ink }}>{a.author}</span>
          <span>·</span><span>{(a.published_at || "").slice(0, 10)}</span>
          <span>·</span><span>{a.reading_time} min read</span>
        </div>
        {a.featured_image && (
          <img src={a.featured_image} alt={a.title} className="w-full max-h-[520px] object-cover mt-8" style={{ border: `1px solid ${C.line}` }} />
        )}
        <div className="mt-10 max-w-2xl mx-auto text-lg leading-relaxed mag-body" style={{ color: C.ink }}
          dangerouslySetInnerHTML={{ __html: a.body }} data-testid="mag-article-body" />
        <style>{`
          .mag-body h2{font-family:'Playfair Display',serif;font-size:1.8rem;font-weight:800;margin:2rem 0 .8rem;letter-spacing:-0.02em}
          .mag-body h3{font-family:'Playfair Display',serif;font-size:1.4rem;font-weight:700;margin:1.5rem 0 .6rem}
          .mag-body p{margin:0 0 1.2rem}
          .mag-body blockquote{font-family:'Playfair Display',serif;font-size:1.6rem;font-style:italic;line-height:1.4;border-left:4px solid #E05C3A;padding-left:1.2rem;margin:2rem 0;color:#1A3626}
          .mag-body img{max-width:100%;margin:1.5rem 0;border:1px solid #D1CCC3}
          .mag-body ul{list-style:disc;padding-left:1.5rem;margin-bottom:1.2rem}
          .mag-body ol{list-style:decimal;padding-left:1.5rem;margin-bottom:1.2rem}
          .mag-body a{color:#E05C3A;text-decoration:underline}
          .mag-body .embed{margin:1.5rem 0}
          .mag-body > p:first-of-type::first-letter{font-family:'Playfair Display',serif;font-size:3.4rem;float:left;line-height:.9;padding-right:.5rem;color:#E05C3A;font-weight:900}
        `}</style>

        <div className="mt-10 pt-6 flex flex-wrap items-center gap-3" style={{ borderTop: `1px solid ${C.line}` }} data-testid="mag-share-bar">
          <span className="text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: C.muted }}>Share</span>
          {[["facebook", Facebook], ["x", Twitter], ["whatsapp", MessageCircle], ["email", Mail]].map(([k, Icon]) => (
            <a key={k} href={share[k]} target="_blank" rel="noreferrer" data-testid={`mag-share-${k}`}
              className="h-9 w-9 flex items-center justify-center transition-colors hover:text-white"
              style={{ border: `1px solid ${C.line}`, color: C.ink2 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = C.terra)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
              <Icon className="h-4 w-4" />
            </a>
          ))}
          <button onClick={copyLink} data-testid="mag-share-copy" className="h-9 w-9 flex items-center justify-center" style={{ border: `1px solid ${C.line}`, color: C.ink2 }}>
            <Link2 className="h-4 w-4" />
          </button>
        </div>
      </article>

      {a.related?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-16" data-testid="mag-related">
          <SectionTitle>Related Stories</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {a.related.map((r) => <ArticleCard key={r.article_id} a={r} slug={pubSlug} />)}
          </div>
        </section>
      )}
      <SubscribeBand slug={pubSlug} />
      <RadioPlayerBar stationId={a.radio_station_id} />
    </div>
  );
}
